import {
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import { useFormatter } from "@/utils/useFormatter";
import {
  getRelatedPersonIdentifierValue,
  getRelatedPersonResourceId,
} from "@/services/fhir/relatedperson/relatedPersonVerification";

interface RelatedPersonVerificationListProps {
  entries: any[];
  isRowLoading: (resourceId: string) => boolean;
  isVerifyLoading: (resourceId: string) => boolean;
  isDenyLoading: (resourceId: string) => boolean;
  onVerify: (entry: any) => void;
  onDeny: (entry: any) => void;
  title?: string;
  excludeIds?: string[];
}

export function RelatedPersonVerificationList({
  entries,
  isRowLoading,
  isVerifyLoading,
  isDenyLoading,
  onVerify,
  onDeny,
  title,
  excludeIds = [],
}: RelatedPersonVerificationListProps) {
  const visibleEntries = entries.filter(
    (entry) => !excludeIds.includes(getRelatedPersonResourceId(entry) ?? "")
  );

  if (!visibleEntries.length) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {title ? (
        <Typography variant="body2" color="neutral700.main" fontWeight={500}>
          {title}
        </Typography>
      ) : null}
      {visibleEntries.map((entry, index) => {
        const resourceId = getRelatedPersonResourceId(entry);
        const rowLoading = Boolean(resourceId && isRowLoading(resourceId));
        const verifyLoading = Boolean(
          resourceId && isVerifyLoading(resourceId)
        );
        const denyLoading = Boolean(resourceId && isDenyLoading(resourceId));
        const identifier = getRelatedPersonIdentifierValue(entry);

        return (
          <Stack
            key={resourceId ?? index}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body1" color="neutral700.main" fontWeight={500}>
              {entry.resource?.name?.[0]?.text}
              {identifier ? ` - ${useFormatter.formatCPF(identifier)}` : ""}
            </Typography>
            <Stack direction="row">
              <Tooltip title="Verificar">
                <span>
                  <IconButton
                    color="primary"
                    disabled={entry?.resource?.active || rowLoading}
                    onClick={() => onVerify(entry)}
                  >
                    {verifyLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Check />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Não verificar">
                <span>
                  <IconButton
                    sx={{
                      color: "error600.main",
                      "&:hover": {
                        background: "error700.main",
                      },
                    }}
                    disabled={rowLoading}
                    onClick={() => onDeny(entry)}
                  >
                    {denyLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Close />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}
